import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { ShipmentEntry, StorageType } from '../../types/shipment';
import { ingredientReceivingApiService } from '../../services/ingredientReceivingApiService';
import { ingredientPackageSizeApiService } from '../../services/ingredientPackageSizeApiService';
import { addTraceCode, removeTraceCodesByIds } from './ingredientTraceCodesSlice';
import type { RootState } from '../store';

// State structure with loading and error
interface IngredientReceivingState {
  entries: ShipmentEntry[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: IngredientReceivingState = {
  entries: [],
  loading: false,
  error: null
};

// Helper function to create a blank entry
export function createBlankReceivingEntry(): ShipmentEntry {
  // Get local date to avoid UTC timezone shift
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`; // YYYY-MM-DD

  return {
    id: uuidv4(),
    supplierName: '',
    supplierId: '',
    ingredient: '',
    lotNumber: '',
    quantity: 0,
    unit: 'lbs',
    conditionUponArrival: '',
    receivingTemperature: 0,
    temperatureUnit: 'F',
    receivedBy: '',
    correctiveAction: '',
    storageType: 'non_refrigerated',
    frozenSolidConfirmed: null,
    receivingDate: dateString,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

// Async thunks

/**
 * Fetch all ingredient receiving entries from the API
 */
export const fetchIngredientReceivingEntries = createAsyncThunk(
  'ingredientReceiving/fetchEntries',
  async () => {
    return await ingredientReceivingApiService.fetchIngredientReceivingEntries();
  }
);

/**
 * Fetch ingredient receiving entries filtered by supplier ID
 */
export const fetchIngredientReceivingBySupplier = createAsyncThunk(
  'ingredientReceiving/fetchBySupplier',
  async (supplierId: string) => {
    return await ingredientReceivingApiService.fetchIngredientReceivingBySupplier(supplierId);
  }
);

/**
 * Fetch a single ingredient receiving entry by ID
 */
export const fetchIngredientReceivingEntryById = createAsyncThunk(
  'ingredientReceiving/fetchEntryById',
  async (id: string) => {
    return await ingredientReceivingApiService.fetchIngredientReceivingEntryById(id);
  }
);

/**
 * Create a new ingredient receiving entry
 * Also dispatches the auto-created trace code to update trace codes state
 */
export const createIngredientReceivingEntry = createAsyncThunk(
  'ingredientReceiving/createEntry',
  async (entry: ShipmentEntry, { dispatch }) => {
    console.log('[DEBUG createIngredientReceivingEntry] Starting API call...');
    const result = await ingredientReceivingApiService.createIngredientReceivingEntry(entry);
    console.log('[DEBUG createIngredientReceivingEntry] API result:', result);
    console.log('[DEBUG createIngredientReceivingEntry] Trace code from API:', result.traceCode);

    // Dispatch the trace code to the trace codes slice if it was created
    if (result.traceCode) {
      console.log('[DEBUG createIngredientReceivingEntry] Dispatching addTraceCode with:', result.traceCode);
      dispatch(addTraceCode(result.traceCode));
    } else {
      console.log('[DEBUG createIngredientReceivingEntry] ⚠️ No trace code in result!');
    }

    return result.entry;
  }
);

/**
 * Update an existing ingredient receiving entry
 */
export const updateIngredientReceivingEntry = createAsyncThunk(
  'ingredientReceiving/updateEntry',
  async (entry: ShipmentEntry) => {
    return await ingredientReceivingApiService.updateIngredientReceivingEntry(entry);
  }
);

/**
 * Delete an ingredient receiving entry by ID
 * Also removes CASCADE deleted trace codes from trace codes state
 */
export const deleteIngredientReceivingEntry = createAsyncThunk(
  'ingredientReceiving/deleteEntry',
  async (id: string, { dispatch }) => {
    console.log('[DEBUG deleteIngredientReceivingEntry] Deleting entry:', id);
    const deletedTraceCodeIds = await ingredientReceivingApiService.deleteIngredientReceivingEntry(id);
    console.log('[DEBUG deleteIngredientReceivingEntry] Deleted trace code IDs from API:', deletedTraceCodeIds);

    // Dispatch to remove the trace codes from Redux state
    if (deletedTraceCodeIds && deletedTraceCodeIds.length > 0) {
      console.log('[DEBUG deleteIngredientReceivingEntry] Dispatching removeTraceCodesByIds');
      dispatch(removeTraceCodesByIds(deletedTraceCodeIds));
    } else {
      console.log('[DEBUG deleteIngredientReceivingEntry] ⚠️ No trace codes to remove');
    }

    return id; // Return id for reducer to remove from state
  }
);

/**
 * Propagate a storage type change to all entries with the same ingredient name.
 * Also updates the supplier ingredient record.
 */
export const propagateStorageTypeChange = createAsyncThunk(
  'ingredientReceiving/propagateStorageType',
  async (
    { ingredientName, storageType, supplierId }: { ingredientName: string; storageType: StorageType; supplierId: string },
    { getState }
  ) => {
    const state = getState() as RootState;
    const entries = state.ingredientReceiving.entries;

    // Find all other entries with the same ingredient that need updating
    const entriesToUpdate = entries.filter(
      e => e.ingredient === ingredientName && e.storageType !== storageType
    );

    // Update each entry via API
    for (const entry of entriesToUpdate) {
      await ingredientReceivingApiService.updateIngredientReceivingEntry({
        ...entry,
        storageType
      });
    }

    // Update the supplier ingredient record
    try {
      const supplierIngredient = await ingredientPackageSizeApiService.findOrCreateSupplierIngredient(supplierId, ingredientName);
      await ingredientPackageSizeApiService.updateStorageType(supplierIngredient.id, storageType);
    } catch (error) {
      console.error('Failed to update supplier ingredient storage type:', error);
    }

    return { ingredientName, storageType };
  }
);

const ingredientReceivingSlice = createSlice({
  name: 'ingredientReceiving',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch all entries
    builder
      .addCase(fetchIngredientReceivingEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIngredientReceivingEntries.fulfilled, (state, action) => {
        state.loading = false;
        console.log('[Redux] Received entries from API:', action.payload.length);
        if (action.payload.length > 0) {
          console.log('[Redux] Sample entry:', action.payload[0]);
        }
        state.entries = action.payload;
      })
      .addCase(fetchIngredientReceivingEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch ingredient receiving entries';
      });

    // Fetch entries by supplier
    builder
      .addCase(fetchIngredientReceivingBySupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIngredientReceivingBySupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload;
      })
      .addCase(fetchIngredientReceivingBySupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch ingredient receiving entries by supplier';
      });

    // Fetch entry by ID
    builder
      .addCase(fetchIngredientReceivingEntryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIngredientReceivingEntryById.fulfilled, (state, action) => {
        state.loading = false;
        // Update the entry in the array if it exists, otherwise add it
        const index = state.entries.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.entries[index] = action.payload;
        } else {
          state.entries.push(action.payload);
        }
      })
      .addCase(fetchIngredientReceivingEntryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch ingredient receiving entry';
      });

    // Create entry
    builder
      .addCase(createIngredientReceivingEntry.fulfilled, (state, action) => {
        state.entries.push(action.payload);
      })
      .addCase(createIngredientReceivingEntry.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create ingredient receiving entry';
      });

    // Update entry
    builder
      .addCase(updateIngredientReceivingEntry.fulfilled, (state, action) => {
        const index = state.entries.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.entries[index] = action.payload;
        }
      })
      .addCase(updateIngredientReceivingEntry.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update ingredient receiving entry';
      });

    // Delete entry
    builder
      .addCase(deleteIngredientReceivingEntry.fulfilled, (state, action) => {
        state.entries = state.entries.filter(e => e.id !== action.payload);
      })
      .addCase(deleteIngredientReceivingEntry.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete ingredient receiving entry';
      });

    // Propagate storage type change
    builder
      .addCase(propagateStorageTypeChange.fulfilled, (state, action) => {
        const { ingredientName, storageType } = action.payload;
        state.entries.forEach(entry => {
          if (entry.ingredient === ingredientName) {
            entry.storageType = storageType;
          }
        });
      });
  }
});

export default ingredientReceivingSlice.reducer;
